import { lazy, Suspense, useEffect, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { TwcBackground } from '@atlassian/product-wireframes';
import { SOURCE_COMPONENT, SOURCE_CONTEXT } from '@atlassian/twc-xflow-modal/constants';

import { useAnalyticsClient } from '@/analytics/wac-analytics/analytics-client';
import { TwcOverlayTemplate } from '@/components/signup/layouts/product-overlay-template';
import { Dropdown } from '@/components/signup/site-activation/components/common/dropdown';
import { useSiteActivationContext } from '@/components/signup/site-activation/hooks/site-activation-context';
import { SiteActivation } from '@/components/signup/site-activation/site-activation';
import { isProduction } from '@/components/signup/utils/environment';
import { useUpliftableSites } from '@/components/user-context/helpers/cohorts';
import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import {
	ExistingSite,
	GetSignupAccountOrgDetailsResponse,
} from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { Button } from '@/libraries/design/components/button/src/button';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';
import { useLocation } from '@/utils/location-provider';

import { Headings } from '../../components/headings';
import { SwitchYourSiteMessage } from '../components/switch-your-site-message';
import { getLoomTwcWelcomeUrl } from '../loom-twc-welcome-url';

const TwcXflowModal = lazy(() =>
	import('@atlassian/twc-xflow-modal').then(({ TwcXflowModal }) => ({ default: TwcXflowModal })),
);

export const UpliftEligibleUser = () => {
	const [xflowModalTarget, setXflowModalTarget] = useState<{
		cloudId: string;
		orgId?: string;
	} | null>(null);
	const { locale } = useIntl();
	const switchYourSiteText = (
		<FormattedMessage
			id="signup.teamworkCollection.uplift.heading"
			defaultMessage="Switch your site to Teamwork Collection"
		/>
	);

	if (xflowModalTarget) {
		return (
			<TwcBackground>
				<Suspense fallback={null}>
					<TwcXflowModal
						cloudId={xflowModalTarget.cloudId}
						locale={locale}
						onClose={() => setXflowModalTarget(null)}
						onComplete={() =>
							window.location.assign(
								getLoomTwcWelcomeUrl(xflowModalTarget.cloudId, { skipUpgrade: true }),
							)
						}
						orgId={xflowModalTarget.orgId}
						sourceComponent={SOURCE_COMPONENT.WAC}
						sourceContext={SOURCE_CONTEXT.WAC_HOMEPAGE_TWC_OFFER}
						testId="wac-twc-xflow-modal"
					/>
				</Suspense>
			</TwcBackground>
		);
	}

	return (
		<TwcOverlayTemplate size="sm">
			<Headings main={switchYourSiteText} />
			<SiteActivation UpliftEligibleUser={<UpliftPicker onOpenXflow={setXflowModalTarget} />} />
		</TwcOverlayTemplate>
	);
};

const UpliftPicker = ({
	onOpenXflow,
}: {
	onOpenXflow: (target: { cloudId: string; orgId?: string }) => void;
}) => {
	const [loading, setLoading] = useState(true);
	const [orgs, setOrgs] = useState<GetSignupAccountOrgDetailsResponse[]>([]);
	const { sitePickerValue, setSitePickerValue } = useSiteActivationContext();
	const potentiallyUpliftableSites = useUpliftableSites();
	const [upliftableSites, setUpliftableSites] = useState<ExistingSite[]>([]);
	const stargateClient = useBxpWebStargateClient();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsClient = useAnalyticsClient();
	const location = useLocation();
	const shouldOpenCrossflow =
		checkFeatureFlagGate(featureFlags.twcg1930TwcWacHomepage) &&
		new URLSearchParams(location.search).has('openTwcXflowModal');

	useEffect(() => {
		if (
			shouldOpenCrossflow &&
			upliftableSites.length > 0 &&
			!upliftableSites.some(({ cloudId }) => cloudId === sitePickerValue?.cloudId)
		) {
			setSitePickerValue(upliftableSites[0]);
		}
	}, [setSitePickerValue, shouldOpenCrossflow, sitePickerValue?.cloudId, upliftableSites]);

	const onSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!sitePickerValue) return;
		const cloudId = sitePickerValue.cloudId;
		const selectedOrg = orgs.find((org) => org.organization?.siteIds?.includes(cloudId));
		const orgId = selectedOrg?.organization?.id;

		createAnalyticsEvent({
			eventType: 'ui',
			action: 'submitted',
			actionSubject: 'twcUpliftForm',
		}).fire();

		if (shouldOpenCrossflow) {
			onOpenXflow({ cloudId, orgId });
			return;
		}

		const adminHubUrl = isProduction()
			? 'https://admin.atlassian.com'
			: 'https://admin.stg.atlassian.com';
		const upgradeUrl = `${adminHubUrl}/o/${orgId}/atlassian-apps?openAddAppDrawer=TeamworkCollection`;
		window.location.assign(upgradeUrl);
	};

	useEffect(() => {
		if (!potentiallyUpliftableSites) {
			return;
		}
		if (shouldOpenCrossflow) {
			setUpliftableSites(potentiallyUpliftableSites);
		}
		stargateClient
			.getAccountOrgs({})
			.then(async ({ accountOrgs }) => {
				if (!accountOrgs) {
					return;
				}
				const orgIds = accountOrgs.filter(({ isAdmin }) => isAdmin).map(({ id }) => id);
				const orgDetails = await Promise.all(
					orgIds.map((orgId) => stargateClient.getSignupAccountOrgDetails({ orgId })),
				);
				const eligibleCloudIds = orgDetails.flatMap((org) => org.organization?.siteIds ?? []);
				const eligibleSites = potentiallyUpliftableSites.filter((site) =>
					eligibleCloudIds.includes(site.cloudId),
				);
				setOrgs(orgDetails);
				if (!shouldOpenCrossflow) {
					setUpliftableSites(eligibleSites);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	}, [stargateClient, potentiallyUpliftableSites, shouldOpenCrossflow]);

	useEffect(() => {
		if (loading || !analyticsClient) {
			return;
		}
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'viewed',
			actionSubject: 'twcUpliftScreen',
			attributes: {
				eligibleSitesCount: upliftableSites.length,
			},
		}).fire();
	}, [loading, analyticsClient, createAnalyticsEvent, upliftableSites.length]);

	if (loading) {
		return (
			<div css={spinnerContainerCss}>
				<Spinner size="large" />
			</div>
		);
	}

	if (!upliftableSites.length) {
		return <SwitchYourSiteMessage />;
	}

	return (
		<form css={formCss} onSubmit={onSubmit}>
			<Dropdown sites={upliftableSites} />
			<Button css={buttonCss} type="submit">
				<FormattedMessage
					id="siteActivation.nameSiteNewUser.continueButton"
					defaultMessage="Continue"
				/>
			</Button>
		</form>
	);
};

const formCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.300', '24px'),
	width: '100%',
});

const buttonCss = css({
	maxWidth: 'none',
});

const spinnerContainerCss = css({
	alignItems: 'center',
	display: 'flex',
	flexGrow: 1,
	justifyContent: 'center',
	padding: 40,
});
